import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AlertTriangle, MoreHorizontal, Pencil, Plus, Search, Users } from "lucide-react";
import { message } from "antd";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { ClientFormDialog } from "@/components/clients/ClientFormDialog";
import { TenantStatusInline } from "@/pages/clients/TenantStatusInline";
import { tenantStatusRosterFilterValue, tenantStatusRosterSortValue } from "@/pages/clients/tenantStatusPresentation";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { TenantStatusRowEdit } from "@/pages/clients/TenantStatusRowEdit";
import { apiClient } from "@/services/apiClient";
import type { TenantStatus } from "@/types/tenant";
import {
  FilterBuilder,
  SortableTh,
  TablePagination,
  applyFilterRules,
  useColumnResize,
  usePagination,
  useTableSort,
  type FilterColumn,
  type FilterConjunction,
  type FilterRule,
  type SortAccessors,
} from "@/components/ui/data-table";
import { cn } from "@/lib/utils";
import { useWorkspaceStore } from "@/stores/useWorkspaceStore";
import {
  clientsService,
  type ContractDraft,
  type LifecycleAction,
  type RosterClient,
} from "@/services/clientsService";

/**
 * C2 of the clients consolidation: ONE roster showing a client whole —
 * contract ⋈ tenant ⋈ workspace — with the P1 identity audit's gap badges
 * built in. On the shared data-table toolkit (sort/resize/filter/paging)
 * like every other roster-sized table; a swallowed fetch error must render
 * as an error, never as an empty roster (the staging pool-exhaustion
 * incident hid behind exactly that).
 */

const FLAG_LABEL: Record<string, string> = {
  unmapped_tenant: "no tenant mapped",
  no_workspace: "no workspace",
  no_contract: "no contract",
};

const round2 = (n: number | null) =>
  n == null ? "—" : String(Math.round(n * 100) / 100);

const statusOf = (c: RosterClient) =>
  c.archived ? "archived" : c.is_paused ? "paused" : "active";

const gapsText = (c: RosterClient) =>
  c.flags.map((f) => FLAG_LABEL[f] ?? f).join(", ") || "none";

const SORT_ACCESSORS: SortAccessors<RosterClient> = {
  name: (r) => (r.name ?? "").toLowerCase(),
  tenants: (r) => (r.tenants[0]?.tenant_id ?? "").toLowerCase(),
  tenant_status: (r) => tenantStatusRosterSortValue(r.tenants),
  points: (r) => r.monthly_points,
  budget: (r) => r.monthly_budget_dollars,
  cycle: (r) => r.billing_cycle_start_day,
  ads: (r) => r.ads_customer_id ?? "",
  gaps: (r) => r.flags.length,
};

const RESIZE_DEFAULTS: Record<string, number> = {
  name: 220,
  tenants: 260,
  tenant_status: 160,
  points: 90,
  budget: 110,
  cycle: 80,
  ads: 120,
  gaps: 170,
};

function RowActionsMenu({
  label,
  canEdit,
  canDetails,
  canOpenPlan,
  onEdit,
  onDetails,
  onOpenPlan,
}: {
  /** Row identifier for the aria label; use the row-key fallback so a
   *  contract-only or name-only row does not read "Actions for null"
   *  (Viktor #2638). Empty falls back to "client". */
  label: string;
  canEdit: boolean;
  /** Details opens ClientFormDialog on this contract; contract-less rows
   *  cannot use it and it must not render as a silent no-op (Viktor #2648). */
  canDetails: boolean;
  canOpenPlan: boolean;
  onEdit: () => void;
  onDetails: () => void;
  onOpenPlan: () => void;
}) {
  // One button per row (Jacob, 2026-09-10): the Pencil / Details / Open plan
  // trio was three ghost buttons taking horizontal space. A single
  // MoreHorizontal trigger opens a small menu; Edit stays at the top so
  // muscle memory still lands on the row-edit path.
  // Nothing to show: render no trigger (Viktor #2638: an empty popover was
  // a worse read than no button at all).
  if (!canEdit && !canDetails && !canOpenPlan) return null;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="h-8 px-2"
          aria-label={`Actions for ${label || "client"}`}
        >
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-40">
        {canEdit ? (
          <DropdownMenuItem onSelect={onEdit}>
            <Pencil className="mr-2 h-3.5 w-3.5" />
            Edit
          </DropdownMenuItem>
        ) : null}
        {canDetails ? (
          <DropdownMenuItem onSelect={onDetails}>Details</DropdownMenuItem>
        ) : null}
        {canOpenPlan ? (
          <>
            {(canEdit || canDetails) ? <DropdownMenuSeparator /> : null}
            <DropdownMenuItem onSelect={onOpenPlan}>Open plan</DropdownMenuItem>
          </>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}


export default function AllClientsPage() {
  const navigate = useNavigate();
  const workspaces = useWorkspaceStore((s) => s.workspaces);
  const setCurrentWorkspace = useWorkspaceStore((s) => s.setCurrentWorkspace);

  const [clients, setClients] = useState<RosterClient[]>([]);
  const [gaps, setGaps] = useState<Record<string, number>>({});
  const [loadState, setLoadState] = useState<"loading" | "ready" | "error">("loading");
  const [search, setSearch] = useState("");
  const [showArchived, setShowArchived] = useState(false);

  const load = useCallback(() => {
    setLoadState("loading");
    clientsService
      .roster(showArchived)
      .then((r) => {
        setClients(r.clients);
        setGaps(r.gaps);
        setLoadState("ready");
      })
      .catch(() => setLoadState("error"));
  }, [showArchived]);

  useEffect(() => {
    load();
  }, [load]);

  const { sortKey, sortDir, toggleSort, sortRows } = useTableSort<RosterClient>("name", "asc");
  const { widths, startResize } = useColumnResize(RESIZE_DEFAULTS);
  // Default view is active-only. The seeded rule is what the "Show non-active"
  // toggle removes and re-adds; staff can also just delete it from the filter
  // builder.
  const [rules, setRules] = useState<FilterRule[]>([
    { column: "tenant_status", op: "is", value: "active" },
  ]);
  const [conjunction, setConjunction] = useState<FilterConjunction>("and");
  const [showNonActive, setShowNonActive] = useState(false);

  const toggleShowNonActive = () => {
    setShowNonActive((prev) => {
      const next = !prev;
      setRules((current) => {
        const withoutSeed = current.filter(
          (r) => !(r.column === "tenant_status" && r.op === "is" && r.value === "active"),
        );
        return next
          ? withoutSeed
          : [{ column: "tenant_status", op: "is", value: "active" }, ...withoutSeed];
      });
      return next;
    });
  };

  const filterColumns: FilterColumn<RosterClient>[] = useMemo(
    () => [
      { key: "name", label: "Client", type: "text", accessor: (r) => r.name ?? "" },
      {
        key: "tenants",
        label: "Tenants",
        type: "text",
        accessor: (r) => r.tenants.map((t) => t.tenant_id).join(" "),
      },
      { key: "points", label: "Points", type: "number", accessor: (r) => r.monthly_points },
      { key: "budget", label: "Ad budget", type: "number", accessor: (r) => r.monthly_budget_dollars },
      { key: "cycle", label: "Cycle day", type: "number", accessor: (r) => r.billing_cycle_start_day },
      { key: "ads", label: "Ads CID", type: "text", accessor: (r) => r.ads_customer_id ?? "" },
      // Multi-flag rows join their labels, so "contains" filters each gap.
      { key: "gaps", label: "Gaps", type: "text", accessor: gapsText },
      {
        key: "contract_state",
        label: "Contract state",
        type: "enum",
        accessor: statusOf,
        options: [
          { value: "active", label: "active" },
          { value: "paused", label: "paused" },
          { value: "archived", label: "archived" },
        ],
      },
      {
        key: "tenant_status",
        label: "Status",
        type: "enum",
        // Any-of Active keeps a mixed-tenant contract visible under the
        // default Active-only rule; tenant-less rows stay visible too (Viktor,
        // #2632). See tenantStatusRosterFilterValue for the full rule.
        accessor: (r) => tenantStatusRosterFilterValue(r.tenants),
        options: [
          { value: "active", label: "active" },
          { value: "paused", label: "paused" },
          { value: "offboarded", label: "offboarded" },
        ],
      },
      {
        key: "workspace",
        label: "Has workspace",
        type: "enum",
        accessor: (r) => (r.tenants.some((t) => t.workspace) ? "yes" : "no"),
        options: [
          { value: "yes", label: "yes" },
          { value: "no", label: "no" },
        ],
      },
    ],
    [],
  );

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    const searched = q
      ? clients.filter((c) =>
          `${c.name ?? ""} ${c.tenants.map((t) => t.tenant_id).join(" ")}`
            .toLowerCase()
            .includes(q),
        )
      : clients;
    const filtered = applyFilterRules(searched, rules, filterColumns, conjunction);
    return sortRows(filtered, SORT_ACCESSORS);
  }, [clients, search, rules, filterColumns, conjunction, sortRows]);

  const { page, pageSize, setPage, setPageSize, pageCount, slice } = usePagination(rows.length, {
    defaultPageSize: 20,
  });
  const pageRows = slice(rows);

  // C6 authoring: selection (contract-bearing rows only) + create dialog +
  // row-level inline edit (supersedes the edit-via-modal path; see
  // ai-docs/google-ads/clients-row-inline-edit.md).
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [createOpen, setCreateOpen] = useState(false);
  // Details dialog: keep ClientFormDialog reachable in edit mode for the
  // fields the row doesn't cover (website, start_date, main_poc,
  // contact_email, client_email, client_phone). Pencil is fast row-edit;
  // Details opens the same dialog New client uses (Viktor #2634).
  const [detailsId, setDetailsId] = useState<string | null>(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [editingRowId, setEditingRowId] = useState<string | null>(null);
  const [draft, setDraft] = useState<Partial<ContractDraft>>({});
  const [tenantStatusDraft, setTenantStatusDraft] = useState<Record<string, TenantStatus>>({});
  const [savingRow, setSavingRow] = useState(false);

  // Stable id for edit-mode tracking: contract_id when present, else
  // website (so a contract-less tenant row can still enter edit mode).
  const rowEditKey = (c: RosterClient): string | null =>
    c.contract_id ?? c.website ?? null;

  const beginEdit = (c: RosterClient) => {
    const key = rowEditKey(c);
    if (!key) return;
    setEditingRowId(key);
    setDraft({
      name: c.name ?? "",
      monthly_points: c.monthly_points,
      monthly_budget_dollars: c.monthly_budget_dollars,
      billing_cycle_start_day: c.billing_cycle_start_day ?? 1,
    });
    setTenantStatusDraft({});
  };

  const cancelEdit = () => {
    setEditingRowId(null);
    setDraft({});
    setTenantStatusDraft({});
  };

  const numOrNull = (v: unknown): number | null => {
    if (v === null || v === undefined) return null;
    if (typeof v === "number") return Number.isFinite(v) ? v : null;
    const s = String(v).trim();
    if (s === "") return null;
    const n = Number(s);
    return Number.isFinite(n) ? n : null;
  };

  /** True when v is empty or a valid number; false for '1,200', 'abc'. */
  const looksNumeric = (v: unknown): boolean => {
    if (v === null || v === undefined) return true;
    const s = String(v).trim();
    if (s === "") return true;
    // Reject anything that is not a plain number: '1,200', '$1200', '3px'.
    // Neither Points nor Ad budget has a legitimate negative (Viktor #2634).
    if (!/^\d+(?:\.\d+)?$/.test(s)) return false;
    return Number.isFinite(Number(s));
  };

  const contractDiff = (c: RosterClient): Partial<ContractDraft> => {
    const diff: Partial<ContractDraft> = {};
    const nextName = (draft.name ?? "").trim();
    if (nextName !== (c.name ?? "")) diff.name = nextName;
    const nextPoints = numOrNull(draft.monthly_points);
    if (nextPoints !== (c.monthly_points ?? null)) diff.monthly_points = nextPoints;
    const nextBudget = numOrNull(draft.monthly_budget_dollars);
    if (nextBudget !== (c.monthly_budget_dollars ?? null))
      diff.monthly_budget_dollars = nextBudget;
    const nextCycle = numOrNull(draft.billing_cycle_start_day);
    if (nextCycle !== (c.billing_cycle_start_day ?? null))
      diff.billing_cycle_start_day = nextCycle;
    return diff;
  };

  const saveRow = async (c: RosterClient) => {
    // A contract-less row is Status-only. Only the contract path needs a
    // name (Viktor #2645: contract PATCH would fail without one).
    if (c.contract_id && !(draft.name ?? "").trim()) {
      message.warning("Client name is required");
      return;
    }
    // Reject bad numeric input before any write (Viktor #2634): a stray
    // '1,200' or '$1200' would coerce to NaN then to null and silently
    // wipe the field. Only empty means null.
    if (!looksNumeric(draft.monthly_points)) {
      message.warning("Points must be a number");
      return;
    }
    if (!looksNumeric(draft.monthly_budget_dollars)) {
      message.warning("Ad budget must be a number, no commas or currency");
      return;
    }
    setSavingRow(true);
    // No contract on this row: only status writes are possible.
    const diff = c.contract_id ? contractDiff(c) : {};
    const contractHadWrite = Boolean(c.contract_id) && Object.keys(diff).length > 0;
    try {
      if (contractHadWrite && c.contract_id) {
        try {
          await clientsService.update(c.contract_id, diff);
          // Merge the landed diff into local state so a retry Save after
          // a status failure does not re-PATCH the contract with the same
          // values (Viktor H2 correction, #2634).
          setClients((prev) =>
            prev.map((row) =>
              row.contract_id === c.contract_id ? { ...row, ...diff } : row,
            ),
          );
        } catch (err) {
          const status = (err as { response?: { status?: number } })?.response?.status;
          if (status === 503) {
            message.error(
              "Contract edit is disabled on this environment (PLANNER_CONTRACT_AUTHORING_ENABLED)",
            );
            return;
          }
          throw err;
        }
      }

      // Per-tenant status writes: only those whose draft differs from the row.
      const statusChanges = c.tenants.filter((t) => {
        const next = tenantStatusDraft[t.tenant_id];
        return next && next !== (t.status ?? null);
      });
      const landed: string[] = [];
      const failed: { tenant: string; error: string }[] = [];
      for (const t of statusChanges) {
        const next = tenantStatusDraft[t.tenant_id];
        try {
          await apiClient.patch(
            `/tenant-settings/${encodeURIComponent(t.tenant_id)}/status`,
            { status: next },
          );
          landed.push(t.tenant_id);
        } catch (err) {
          const detail =
            (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
          failed.push({ tenant: t.tenant_id, error: detail ?? "PATCH failed" });
        }
      }

      if (failed.length > 0) {
        if (landed.length > 0) {
          message.error(
            `Landed: ${landed.join(", ")}. Failed: ${failed
              .map((f) => `${f.tenant} (${f.error})`)
              .join("; ")}`,
          );
        } else {
          message.error(
            failed.map((f) => `${f.tenant}: ${f.error}`).join("; "),
          );
        }
        return;
      }

      cancelEdit();
      load();
      const wroteStatus = c.tenants.some((t) => {
        const next = tenantStatusDraft[t.tenant_id];
        return next && next !== (t.status ?? null);
      });
      if (contractHadWrite || wroteStatus) message.success("Client updated");
    } catch (err) {
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      message.error(detail ?? "Save failed");
    } finally {
      setSavingRow(false);
    }
  };

  const toggleRow = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const pageIds = pageRows.filter((c) => c.contract_id).map((c) => c.contract_id as string);
  const allPageSelected = pageIds.length > 0 && pageIds.every((id) => selected.has(id));
  const togglePage = () =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (allPageSelected) pageIds.forEach((id) => next.delete(id));
      else pageIds.forEach((id) => next.add(id));
      return next;
    });

  const runBulk = async (action: LifecycleAction) => {
    setBulkBusy(true);
    try {
      const out = await clientsService.lifecycle([...selected], action);
      if (out.errors.length > 0) {
        message.warning(
          `${action}: ${out.done.length} done, ${out.errors.length} failed (${out.errors[0].error})`,
        );
      } else {
        message.success(`${action}: ${out.done.length} client(s) — Planner first, mirror follows`);
      }
      setSelected(new Set());
      load();
    } catch (err) {
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      message.error(detail ?? `${action} failed`);
    } finally {
      setBulkBusy(false);
    }
  };

  const openCreate = () => {
    setCreateOpen(true);
  };

  const openPlan = (c: RosterClient) => {
    const site = c.tenants[0]?.tenant_id;
    const ws = workspaces.find((w) => w.website === site);
    if (ws) {
      setCurrentWorkspace(ws);
      navigate("/planner");
    }
  };

  const th = (key: string, label: string, className?: string) => (
    <SortableTh
      sortKey={key}
      currentKey={sortKey}
      dir={sortDir}
      onSort={toggleSort}
      resizable
      width={widths[key]}
      onResize={startResize}
      className={className}
    >
      {label}
    </SortableTh>
  );

  return (
    <div className="space-y-4 p-6">
      <div className="flex flex-wrap items-center gap-3">
        <Users className="h-6 w-6 text-coral" />
        <div>
          <h1 className="text-xl font-bold tracking-tight text-foreground">All Clients</h1>
          <p className="text-sm text-muted-foreground">
            Contract, tenants, and workspace in one place — badges mark where the three disagree.
          </p>
        </div>
        <div className="ml-auto flex items-center gap-2 text-xs text-muted-foreground">
          {Object.entries(gaps)
            .filter(([, n]) => n > 0)
            .map(([flag, n]) => (
              <Badge key={flag} variant="outline" className="font-normal text-coral-dark border-coral/40">
                {n} {FLAG_LABEL[flag] ?? flag}
              </Badge>
            ))}
        </div>
      </div>

      <Card className="border-0 shadow-sm">
        <div className="flex flex-wrap items-center gap-2 border-b border-border bg-muted/30 px-4 py-3">
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search clients or domains…"
              className="h-9 w-64 pl-8"
            />
          </div>
          <FilterBuilder
            columns={filterColumns}
            rules={rules}
            conjunction={conjunction}
            onRulesChange={setRules}
            onConjunctionChange={setConjunction}
          />
          <span className="text-xs text-muted-foreground">{rows.length} of {clients.length}</span>
          <div className="ml-auto flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              className="h-8"
              onClick={toggleShowNonActive}
            >
              {showNonActive ? "Hide non-active" : "Show non-active"}
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="h-8"
              onClick={() => setShowArchived((v) => !v)}
            >
              {showArchived ? "Hide archived" : "Show archived"}
            </Button>
            <Button size="sm" className="h-8" onClick={openCreate}>
              <Plus className="mr-1 h-4 w-4" /> New client
            </Button>
          </div>
        </div>

        {selected.size > 0 ? (
          <div className="flex flex-wrap items-center gap-2 border-b border-border bg-muted/40 px-4 py-2">
            <span className="text-xs font-medium">{selected.size} selected</span>
            {(["pause", "unpause", "archive", "unarchive"] as const).map((action) => (
              <Button
                key={action}
                variant={action === "archive" ? "destructive" : "outline"}
                size="sm"
                className="h-7 capitalize"
                disabled={bulkBusy}
                onClick={() => runBulk(action)}
              >
                {action}
              </Button>
            ))}
            <Button
              variant="ghost"
              size="sm"
              className="ml-auto h-7"
              onClick={() => setSelected(new Set())}
            >
              Clear
            </Button>
          </div>
        ) : null}

        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/20">
                <TableHead style={{ width: 40 }}>
                  <Checkbox
                    checked={allPageSelected}
                    onCheckedChange={togglePage}
                    aria-label="Select all on this page"
                  />
                </TableHead>
                {th("name", "Client")}
                {th("tenants", "Tenants / workspace")}
                {th("tenant_status", "Status")}
                {th("points", "Points", "text-right")}
                {th("budget", "Ad budget", "text-right")}
                {th("cycle", "Cycle", "text-right")}
                {th("ads", "Ads")}
                {th("gaps", "Gaps")}
                <TableHead style={{ width: 110 }} aria-label="Actions" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {loadState === "error" ? (
                <TableRow>
                  <TableCell colSpan={10} className="py-10 text-center text-sm">
                    <span className="inline-flex items-center gap-2 text-destructive">
                      <AlertTriangle className="h-4 w-4" /> Couldn't load the roster.
                    </span>
                    <Button variant="outline" size="sm" className="ml-3 h-7" onClick={load}>
                      Retry
                    </Button>
                  </TableCell>
                </TableRow>
              ) : loadState === "loading" ? (
                <TableRow>
                  <TableCell colSpan={10} className="py-10 text-center text-sm text-muted-foreground">
                    Loading clients…
                  </TableCell>
                </TableRow>
              ) : pageRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} className="py-10 text-center text-sm text-muted-foreground">
                    {clients.length === 0
                      ? "No clients in the roster yet."
                      : "No clients match the search / filters."}
                  </TableCell>
                </TableRow>
              ) : (
                pageRows.map((c) => {
                  const isEditing = editingRowId != null && editingRowId === (c.contract_id ?? c.website ?? null);
                  return (
                  <TableRow
                    key={c.contract_id ?? c.website ?? c.name}
                    className={cn(c.is_paused && "opacity-60", c.archived && "opacity-40")}
                  >
                    <TableCell>
                      {c.contract_id ? (
                        <Checkbox
                          checked={selected.has(c.contract_id)}
                          onCheckedChange={() => toggleRow(c.contract_id as string)}
                          aria-label={`Select ${c.name ?? c.website ?? "client"}`}
                          disabled={isEditing}
                        />
                      ) : null}
                    </TableCell>
                    <TableCell className="font-medium">
                      {isEditing && c.contract_id ? (
                        <Input
                          value={draft.name ?? ""}
                          onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
                          aria-label="Client name"
                          className="h-8"
                        />
                      ) : c.contract_id ? (
                        <Link to={`/clients/c/${c.contract_id}`} className="hover:text-coral-dark hover:underline">
                          {c.name || "—"}
                        </Link>
                      ) : (
                        c.name || "—"
                      )}
                      {c.is_paused ? <span className="ml-2 text-xs text-muted-foreground">paused</span> : null}
                      {c.archived ? <span className="ml-2 text-xs text-muted-foreground">archived</span> : null}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-0.5">
                        {c.tenants.length === 0 ? (
                          <span className="text-muted-foreground">—</span>
                        ) : (
                          c.tenants.map((t) => (
                            <span key={t.tenant_id} className="inline-flex items-center gap-1.5 text-sm">
                              <span
                                className={cn(
                                  "inline-block h-1.5 w-1.5 rounded-full",
                                  t.workspace ? "bg-teal" : "bg-destructive/60",
                                )}
                                title={t.workspace ? `Workspace: ${t.workspace.name ?? t.workspace.id}` : "No workspace"}
                              />
                              {t.tenant_id}
                            </span>
                          ))
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-1">
                        {c.tenants.length === 0 ? (
                          <span className="text-muted-foreground">—</span>
                        ) : isEditing ? (
                          c.tenants.map((t) => (
                            <TenantStatusRowEdit
                              key={t.tenant_id}
                              website={t.tenant_id}
                              status={t.status ?? null}
                              value={tenantStatusDraft[t.tenant_id] ?? t.status ?? null}
                              onChange={(next) =>
                                setTenantStatusDraft((s) => ({ ...s, [t.tenant_id]: next }))
                              }
                            />
                          ))
                        ) : (
                          c.tenants.map((t) => (
                            <TenantStatusInline
                              key={t.tenant_id}
                              website={t.tenant_id}
                              status={t.status ?? null}
                              onStatusChanged={load}
                            />
                          ))
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {isEditing && c.contract_id ? (
                        <Input
                          inputMode="decimal"
                          value={draft.monthly_points == null ? "" : String(draft.monthly_points)}
                          onChange={(e) =>
                            setDraft((d) => ({ ...d, monthly_points: e.target.value === "" ? null : (e.target.value as unknown as number) }))
                          }
                          aria-label="Monthly points"
                          className="h-8 text-right"
                        />
                      ) : (
                        round2(c.monthly_points)
                      )}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {isEditing && c.contract_id ? (
                        <Input
                          inputMode="decimal"
                          value={
                            draft.monthly_budget_dollars == null
                              ? ""
                              : String(draft.monthly_budget_dollars)
                          }
                          onChange={(e) =>
                            setDraft((d) => ({
                              ...d,
                              monthly_budget_dollars: e.target.value === "" ? null : (e.target.value as unknown as number),
                            }))
                          }
                          aria-label="Monthly ad budget"
                          className="h-8 text-right"
                        />
                      ) : c.monthly_budget_dollars != null ? (
                        `$${Math.round(c.monthly_budget_dollars).toLocaleString("en-US")}`
                      ) : (
                        "—"
                      )}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {isEditing && c.contract_id ? (
                        // Backend cycle days in use are 1 and 15; if the
                        // current value is something else, render read-only
                        // instead of forcing it to one of the two (Viktor
                        // #2634).
                        c.billing_cycle_start_day == null || c.billing_cycle_start_day === 1 || c.billing_cycle_start_day === 15 ? (
                          <Select
                            value={String(draft.billing_cycle_start_day ?? 1)}
                            onValueChange={(v) =>
                              setDraft((d) => ({ ...d, billing_cycle_start_day: Number(v) }))
                            }
                          >
                            <SelectTrigger className="h-8 w-20" aria-label="Cycle day">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="1">1</SelectItem>
                              <SelectItem value="15">15</SelectItem>
                            </SelectContent>
                          </Select>
                        ) : (
                          <span className="text-muted-foreground">{c.billing_cycle_start_day}</span>
                        )
                      ) : (
                        c.billing_cycle_start_day ?? "—"
                      )}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground tabular-nums">
                      {c.ads_customer_id ?? "—"}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {c.flags.map((f) => (
                          <Badge key={f} variant="outline" className="font-normal text-coral-dark border-coral/40 text-xs">
                            {FLAG_LABEL[f] ?? f}
                          </Badge>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        {isEditing ? (
                          <>
                            <Button
                              size="sm"
                              className="h-8 bg-coral hover:bg-coral-hover"
                              disabled={savingRow}
                              onClick={() => void saveRow(c)}
                            >
                              Save
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8"
                              disabled={savingRow}
                              onClick={cancelEdit}
                            >
                              Cancel
                            </Button>
                          </>
                        ) : (
                          <RowActionsMenu
                            label={c.name ?? c.website ?? c.contract_id ?? "client"}
                            canEdit={(rowEditKey(c) != null) && (Boolean(c.contract_id) || c.tenants.length > 0)}
                            canDetails={Boolean(c.contract_id)}
                            canOpenPlan={c.tenants.some((t) =>
                              workspaces.some((w) => w.website === t.tenant_id),
                            )}
                            onEdit={() => beginEdit(c)}
                            onDetails={() => setDetailsId(c.contract_id ?? null)}
                            onOpenPlan={() => openPlan(c)}
                          />
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      </Card>

      {loadState === "ready" && rows.length > 0 ? (
        <TablePagination
          page={page}
          pageCount={pageCount}
          pageSize={pageSize}
          totalRows={rows.length}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
        />
      ) : null}

      <ClientFormDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        contractId={null}
        onSaved={load}
      />
      <ClientFormDialog
        open={detailsId !== null}
        onOpenChange={(open) => { if (!open) setDetailsId(null); }}
        contractId={detailsId}
        onSaved={load}
      />
    </div>
  );
}
